import { createAction, props } from '@ngrx/store';
import { NavigationExtras } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { type } from '@app/util/util';
import { User, UserInfo } from '@app/modules/core/interfaces/user';
import { Place } from '@app/entities/place';
import { RouterUrlState, HistoryItem, NavigationItem } from '@app/entities/router-state.entity';
import { Toastr } from '@app/entities/toastr';
import { UserAddresses, Address, AddressResponse } from '@app/modules/core/interfaces/address';
import { AlertResponse } from '@shared/widgets/entities/alerts';
import { TrackingState } from '@app/store/reducers/tracking.reducer';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { ProvincesByCity } from '@app/entities/provinces-cities';
import { BillingPayload } from '@billing/entities/billing.payload';
import { AuthSeamlessLoginPayload } from '@app/entities/auth-seamless-login.payload';
import { UserBenefitResponse } from '@shared/benefits/entities/benefits';
import { HeaderProductType } from '@header/entities/ds-header-config';
import { DateWidgetType } from '@shared/magic-filter-bar/date-filter/entities/date-filter';
import { StatsigExperimentsConfig } from '@statsig/statsig.entities';
import { StaticList } from '@app/modules/core/interfaces/static-lists';

export const loadUserDataFromInitAction = createAction(
  type('[Global/API] Load user data from Init'),
  props<{ user: User, userInfo: UserInfo, experiments: StatsigExperimentsConfig[] }>());
export const loadUserDataSuccessAction = createAction(
  type('[Global/API] Load user data success'),
  props<{ user: User }>()
);

export const loadUserInfoSuccessAction = createAction(
  type('[Global/API] Load user info success'),
  props<{ userInfo: UserInfo }>()
);
export const loadUserAddressesSuccessAction = createAction(
  type('[Global/API] Load user address success'),
  props<{ addresses: UserAddresses }>()
);

export const fetchAirportsSuccessAction = createAction(
  type('[Global/API] Fetch airports Success'),
  props<{ airports: string[] }>()
);


export const fetchPlacesSuccessAction = createAction(
  type('[Global/API] Fetch Places Success'),
  props<{ places: Place[] }>()
);

export const fetchBusCompaniesSuccessAction = createAction(
  type('[Global/API] Fetch buses companies success'),
  props<{ busCompanies: string[] }>()
);

export const fetchBusTerminalsSuccessAction = createAction(
  type('[Global/API] Fetch buses terminals success'),
  props<{ busTerminals: string[] }>()
);

export const routerStateAction = createAction(
  type('[Global/API] Router state'),
  props<{ routerState: RouterUrlState }>()
);

export const navigateToAction = createAction(
  type('[Global/API] Navigate to'),
  props<{ commands: Object[], extras: NavigationExtras }>()
);
export const navigateToSuccessAction = createAction(
  type('[Global/API] Navigate to success')
);

export const navigateToLastRouteAction = createAction(
  type('[Global/API] Navigate to last route')
);

export const fetchAirlinesSuccessAction = createAction(
  type('[Global/API] Fetch airlines Success'),
  props<{ airlines: string[] }>()
);

export const fetchMessageAction = createAction(
  type('[Global/Toastr] Fetch message toast'),
  props<{ toastr: Toastr }>()
);
export const fetchMessageSuccessAction = createAction(
  type('[Global/Toastr] Fetch message toast succes')
);

export const removeTokenAction = createAction(
  type('[Global/Datta] Remove token')
);
export const removeTokenSuccessAction = createAction(
  type('[Global/Datta] Remove token success')
);

export const historyPushAction = createAction(
  type('[Global/API] History push'),
  props<{ item: HistoryItem }>()
);
export const historyPopAction = createAction(
  type('[Global/API] History pop')
);
export const historyResetAction = createAction(
  type('[Global/API] History reset')
);

export const setDefaultAddressAction = createAction(
  type('[Billing-Booking/API] Set default address'),
  props<{ address: Address }>()
);

export const fetchUserAlertsAction = createAction(
  type('[Global/Data] Fetch user alerts'),
  props<{ user: User }>()
);
export const fetchUserAlertSuccessAction = createAction(
  type('[Global/Data] Fetch user alerts success'),
  props<{ alerts: AlertResponse[] }>()
);

export const setCloseWebViewInDetailAction = createAction(
  type('[Global/Data] Set close WebView in detail'),
  props<{ payload: string }>()
);
export const resetCloseWebViewInDetailAction = createAction(
  type('[Global/Data] Reset close WebView in detail')
);
export const activeDefaultAddressesAction = createAction(
  type('[Global/API] Active user address'),
  props<{ address: Address }>()
);

export const activeDefaultAddressesSuccessAction = createAction(
  type('[Global/API] Active user address success'),
  props<{ response: Address }>()
);
export const createDefaultAddressesAction = createAction(
  type('[Global/API] Create default address')
);
export const createDefaultAddressesSuccessAction = createAction(
  type('[Global/API] Create default address success'),
  props<{ response: AddressResponse }>()
);

export const fetchFrequentFlyerProgramsSuccessAction = createAction(
  type('[Global/API] Fetch frequent flyer programs'),
  props<{ programs: FrequentFlyerResponse[] }>()
);

export const fetchProvincesAndCitiesExternalSuccessAction = createAction(
  type('[Global/API] Fetch provinces and cities external success'),
  props<{ provinces: ProvincesByCity[] }>()
);

export const setTrackingAction = createAction(
  type('[Global/API] Set Tracking data'),
  props<{ tracking: TrackingState }>()
);

export const initAssetsAction = createAction(
  type('[Global/API] Init application Assets')
);

export const fetchSeamlessLoginTokenAction = createAction(
  type('[Global/API] Fetch seamless login token'),
  props<{ payload: string }>()
);

export const fetchSeamlessLoginTokenSuccessAction = createAction(
  type('[Global/API] Fetch seamless login token success'),
  props<{ response: string }>()
);

export const loggerBookingAction = createAction(
  type('[Global/API] Logger Booking Action'),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props<{ arrangement: any, billing: BillingPayload }>()
);

export const setInternationalSearchAction = createAction(
  type('[Global/UI] Set international search UI action'),
  props<{ payload: boolean }>()
);

export const authenticateTokenAction = createAction(
  type('[Global/Data] Authenticate token'),
  props<{ payload: AuthSeamlessLoginPayload }>()
);

export const fetchPrimeModalEnable = createAction(
  type('[Global/Data] Prime modal Enable'),
  props<{ isEnable: boolean }>()
);

export const setHistoryToSessionStorageAction = createAction(
  type('[Global/API] Set history to Session Storage'),
  props<{ payload: HistoryItem[] }>()
);

export const restoreHistoryAction = createAction(
  type('[Global/API] Restore history')
);

export const restoreHistorySuccessAction = createAction(
  type('[Global/API] Restore history success'),
  props<{ payload: HistoryItem[] }>()
);

export const setHistoryToSessionStorageSuccessAction = createAction(
  type('[Global/API] Set history to Session Storage success')
);

export const setCloseWebviewModal = createAction(
  type('[Global/API] Set Close Webview Modal'),
  props<{ payload: boolean }>());


export const setAppliedDateFilterTypeAction = createAction(
  type('[Global UI/Results/Filters] Set applied date filter type'),
  props<{ payload: DateWidgetType }>()
);

export const sortingHasBeenChangedAction = createAction(
  type('[Global UI/Results/Filters] Sorting has been changed'),
  props<{ payload: boolean }>()
);

export const loadPageDataAction = createAction(
  type('[Global/API] Load page data'),
  props<{ userInfo: UserInfo, user: User, experiments: StatsigExperimentsConfig[], isLoggedIn: boolean, }>()
);

export const loadUserBenefitsSuccessAction = createAction(
  type('[Global/API] Load user benefits success'),
  props<{ benefits: UserBenefitResponse[] }>()
);

export const setEditSearchByProductAction = createAction(
  type('[Global UI/Results/Search] Set edit search by product'),
  props<{ product: HeaderProductType }>()
);

export const searchErrorAction = createAction(
  type('[Global/API] Error on Product Search'),
  props<{ payload: HttpErrorResponse }>()
);

export const addNavigationItemAction = createAction(
  type('[Global UI/Navigation] Add new navigation item'),
  props<{ item: NavigationItem }>()
);

export const removeLastNavigationItemAction = createAction(
  type('[Global UI/Navigation] Remove navigation item')
);

export const resetNavigationAction = createAction(
  type('[Global UI/Navigation] Reset navigation')
);

export const fetchStaticListsAction = createAction('[Global] Fetch Static Lists');

export const fetchStaticListsSuccessAction = createAction(
  '[Global] Fetch Static Lists Success',
  props<{ staticLists: StaticList[]}>()
);

export const fetchStaticListsFailureAction = createAction(
  '[Global] Fetch Static Lists Failure',
  props<{ error: HttpErrorResponse  }>()
);
